import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

import { SelectionStateService } from './selection-state.service';
import { USER_ROLE, isReportRole } from '../types/user-role.types';
import { APP_ROUTES } from '../types/app-routes';

/** Nombres con los que cada módulo declara el id del evento en su ruta. */
const EVENT_ID_PARAMS = ['id', 'eventid', 'eventId'] as const;

function eventIdFrom(route: ActivatedRouteSnapshot): number {
  const raw = EVENT_ID_PARAMS.map((name) => route.paramMap.get(name)).find((value) => value !== null);
  return Number(raw);
}

/**
 * Devuelve el estado solo si el usuario es `sellers`. Espera el `/me` completo:
 * `isSellersUser` sale de ahí, no del rol persistido.
 */
async function sellersState(): Promise<SelectionStateService | null> {
  const state = inject(SelectionStateService);
  await state.whenProfileReady();
  return state.isSellersUser() ? state : null;
}

/** Pantallas globales (dashboard, lista, cumpleaños...): el rol vuelve a `seller` sin evento. */
export const globalSellerContextGuard: CanActivateFn = async () => {
  const state = await sellersState();
  await state?.setGlobalContext();
  return true;
};

/** Pantallas de venta del evento: rol `seller` acotado a ese evento. */
export const sellerEventContextGuard: CanActivateFn = async (route) => {
  const state = await sellersState();
  await state?.setEventContext(USER_ROLE.SELLER, eventIdFrom(route));
  return true;
};

/**
 * Detalle del evento (`event-reports/event/:id/*`). Se llega desde Mis ventas (vendedor)
 * y desde el reporte del Jefe/Admin/Global: si ya hay un rol de reporte de ESTE evento
 * se conserva; si no, rol `seller` acotado al evento como en la venta.
 */
export const eventReportDetailContextGuard: CanActivateFn = async (route) => {
  const state = await sellersState();
  if (!state) {
    return true;
  }
  const eventId = eventIdFrom(route);
  const keepsReportRole = isReportRole(state.selectedRole()) && state.activeEventId() === eventId;
  if (!keepsReportRole) {
    await state.setEventContext(USER_ROLE.SELLER, eventId);
  }
  return true;
};

/** Scanner del evento: rol `scanner` acotado a ese evento. El recepcionista no cambia. */
export const scannerEventContextGuard: CanActivateFn = async (route) => {
  const state = await sellersState();
  await state?.setEventContext(USER_ROLE.SCANNER, eventIdFrom(route));
  return true;
};

/**
 * Reporte del evento: solo valida. El rol lo fija el botón de la lista o el chip del header;
 * un rol de otro evento (o sin rol) no puede usarse acá, así que vuelve a la lista.
 */
export const reportEventContextGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const state = await sellersState();
  if (!state) {
    return true;
  }
  const allowed = isReportRole(state.selectedRole()) && state.activeEventId() === eventIdFrom(route);
  return allowed ? true : router.createUrlTree([APP_ROUTES.EVENTS_SELLER]);
};
